import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL fragment (callback from Emergent Auth)
      const hash = window.location.hash;
      console.log('🔍 Checking auth, current hash:', hash);
      
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        console.log('✅ Found session_id in URL:', sessionId);
        console.log('📝 Processing session with backend...');
        // Keep loading true while processing - will redirect to dashboard
        await processSessionId(sessionId);
        return;
      }

      // Check existing session
      console.log('🔐 No session_id in URL, checking existing session cookie...');
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      console.log('✅ User authenticated:', response.data.email);
      setUser(response.data);
    } catch (error) {
      if (error.response) {
        console.log('❌ Not authenticated - Server returned:', error.response.status, error.response.data);
      } else {
        console.log('❌ Not authenticated:', error.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      console.log('Processing session ID from auth callback...');
      console.log('API URL:', API);
      console.log('Session ID:', sessionId);
      
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Session created successfully:', response.data);
      const { user: userData, session_token } = response.data;
      setUser(userData);
      setSessionToken(session_token);
      
      // Set cookie (conditionally set secure flag based on protocol)
      const isHttps = window.location.protocol === 'https:';
      const cookieString = `session_token=${session_token}; path=/; max-age=${7*24*60*60}${isHttps ? '; secure; samesite=lax' : ''}`;
      document.cookie = cookieString;
      console.log('Cookie set successfully. Cookie string:', cookieString);
      
      // Clear the session_id from URL hash to prevent reuse
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      console.log('URL hash cleared, redirecting to dashboard...');
      
      // Redirect to dashboard after successful authentication
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('❌ FAILED TO PROCESS SESSION');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      
      // Log detailed error response
      if (error.response) {
        console.error('API Response Status:', error.response.status);
        console.error('API Response Data:', error.response.data);
        console.error('API Response Headers:', error.response.headers);
        
        // Show user-friendly error message
        const errorDetail = error.response.data?.detail || 'Session validation failed';
        alert(`Authentication failed: ${errorDetail}\n\nThis could mean:\n- The session link expired (they're single-use)\n- The authentication service is unavailable\n- There was a network error\n\nPlease try signing in again.`);
      } else if (error.request) {
        console.error('No response received from server');
        console.error('Request:', error.request);
        alert('Authentication failed: Could not reach the authentication server. Please check your internet connection and try again.');
      } else {
        console.error('Error setting up request:', error.message);
        alert(`Authentication failed: ${error.message}`);
      }
      
      // Clear the session_id from URL hash to prevent retry with expired session
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      console.log('URL hash cleared after error');
      
      setLoading(false);
      
      // Redirect to home page after error
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    }
  };

  const login = () => {
    const redirectUrl = `${window.location.origin}/`;
    // Use fixed Emergent auth domain (not environment-dependent)
    const authUrl = 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      document.cookie = 'session_token=; path=/; max-age=0';
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, sessionToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;